import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Paper,
  Divider,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Add, 
  Edit, 
  Delete,
  TrendingUp,
  AttachMoney,
  Casino,
  Person,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { galiAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const GameRateCard = ({ gameMode, betCategory, configKey, rate, betAmount, winAmount, color }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
  >
    <Card
      sx={{
        background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
        border: `2px solid ${color}30`,
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Avatar sx={{ bgcolor: color, width: 50, height: 50 }}>
            <Casino />
          </Avatar>
          <Chip 
            label={rate} 
            sx={{ 
              bgcolor: color, 
              color: 'white',
              fontSize: '1rem',
              fontWeight: 'bold'
            }} 
          />
        </Box>
        <Box mb={2}>
          <Typography variant="h6" gutterBottom sx={{ textTransform: 'uppercase', color }}>
            {gameMode}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
            {betCategory}
            {configKey && configKey !== 'DEFAULT' && (
              <Chip 
                label={configKey} 
                size="small" 
                sx={{ ml: 1, fontSize: '0.7rem' }} 
              />
            )}
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box display="flex" justifyContent="space-between" mt={2}>
          <Box textAlign="center">
            <Typography variant="h5" sx={{ color, fontWeight: 'bold' }}>
              ₹{betAmount}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Bet Amount
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h5" sx={{ color, fontWeight: 'bold' }}>
              ₹{winAmount}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Win Amount
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" mt={2} textAlign="center">
          User will get ₹{winAmount} for ₹{betAmount}
        </Typography>
      </CardContent>
    </Card>
  </motion.div>
);

const GaliGameRates = () => {
  const { user } = useAuth();
  const [gameRates, setGameRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [formData, setFormData] = useState({
    game_mode: 'JANTRI',
    bet_category: 'SINGLE',
    config_key: 'DEFAULT',
    bet_amount: '10',
    win_amount: '1000',
    rate_display: '1:100',
    status: 'active'
  });
  const [currentFilter, setCurrentFilter] = useState('all');
  
  // Function to calculate rate display based on bet and win amounts
  const calculateRateDisplay = (betAmount, winAmount) => {
    if (!betAmount || !winAmount || betAmount <= 0) return "1:0";
    const ratio = Math.round((winAmount / betAmount) * 100) / 100;
    return `1:${ratio}`;
  };

  useEffect(() => {
    fetchGameRates();
  }, []);

  // Update rate_display when bet_amount or win_amount changes
  useEffect(() => {
    const betAmount = parseFloat(formData.bet_amount);
    const winAmount = parseFloat(formData.win_amount);
    if (betAmount > 0 && winAmount >= 0) {
      setFormData(prev => ({
        ...prev,
        rate_display: calculateRateDisplay(betAmount, winAmount)
      }));
    }
  }, [formData.bet_amount, formData.win_amount]);

  const fetchGameRates = async () => {
    try {
      setLoading(true);
      const response = await galiAPI.getGaliGameRates();
      console.log("API Response:", response.data);
      
      // Use rates array from the response
      const validRates = response.data.rates || [];
      setGameRates(validRates);
    } catch (error) {
      console.error("Error fetching rates:", error);
      toast.error(error.response?.data?.message || 'Failed to fetch game rates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRate = async () => {
    try {
      await galiAPI.createGaliGameRate(formData);
      toast.success('Game rate created successfully');
      setCreateDialog(false);
      resetForm();
      fetchGameRates();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create game rate');
    }
  };

  const handleEditRate = async () => {
    try {
      await galiAPI.updateGaliGameRate(selectedRate.id, formData);
      toast.success('Game rate updated successfully');
      setEditDialog(false);
      setSelectedRate(null);
      fetchGameRates();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update game rate');
    }
  };

  const handleDeleteRate = async (id) => {
    if (window.confirm('Are you sure you want to delete this game rate?')) {
      try {
        await galiAPI.deleteGaliGameRate(id);
        toast.success('Game rate deleted successfully');
        fetchGameRates();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to delete game rate');
      }
    }
  };

  const openEditDialog = (rate) => {
    setSelectedRate(rate);
    setFormData({
      game_mode: rate.game_mode,
      bet_category: rate.bet_category,
      config_key: rate.config_key || 'DEFAULT',
      bet_amount: rate.bet_amount.toString(),
      win_amount: rate.win_amount.toString(),
      rate_display: rate.rate_display,
      status: rate.status
    });
    setEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      game_mode: 'JANTRI',
      bet_category: 'SINGLE',
      config_key: 'DEFAULT',
      bet_amount: '10',
      win_amount: '1000',
      rate_display: '1:100',
      status: 'active'
    });
  };

  const getGameModeColor = (gameMode) => {
    switch (gameMode) {
      case 'JANTRI': return '#6366f1';
      case 'CROSS': return '#10b981';
      case 'OPEN': return '#f59e0b';
      default: return '#6366f1';
    }
  };

  const getBetCategoryColor = (category) => {
    switch (category) {
      case 'SINGLE': return '#a855f7';
      case 'ANDAR': return '#ec4899';
      case 'BAHAR': return '#3b82f6';
      case 'NUMBER': return '#14b8a6';
      case 'HARUP': return '#f97316';
      default: return '#6366f1';
    }
  };

  // Generate the columns for the DataGrid with fields matching API response
  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'game_mode', 
      headerName: 'Game Mode', 
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value.toUpperCase()}
          sx={{ 
            bgcolor: getGameModeColor(params.value), 
            color: 'white',
            fontWeight: 'bold'
          }}
        />
      )
    },
    { 
      field: 'bet_category', 
      headerName: 'Category', 
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          sx={{ 
            bgcolor: getBetCategoryColor(params.value), 
            color: 'white',
          }}
          size="small"
        />
      )
    },
    {
      field: 'config_key',
      headerName: 'Config',
      width: 130,
      renderCell: (params) => (
        params.value !== 'DEFAULT' ? (
          <Chip label={params.value} size="small" variant="outlined" />
        ) : (
          <Typography variant="body2" color="text.secondary">Default</Typography>
        )
      )
    },
    {
      field: 'bet_amount',
      headerName: 'Bet Amount',
      width: 120,
      renderCell: (params) => `₹${params.value}`,
    },
    {
      field: 'win_amount',
      headerName: 'Win Amount',
      width: 120,
      renderCell: (params) => `₹${params.value}`,
    },
    {
      field: 'rate_display',
      headerName: 'Rate',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'active' ? 'success' : 'error'}
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: 'creator',
      headerName: 'Created By',
      width: 120,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
            <Person fontSize="small" />
          </Avatar>
          <Typography variant="body2">
            {params.value?.username || 'Unknown'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton 
              size="small" 
              onClick={() => openEditDialog(params.row)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          {user.user_type === 'owner' && (
            <Tooltip title="Delete">
              <IconButton 
                size="small" 
                onClick={() => handleDeleteRate(params.row.id)}
                color="error"
              >
                <Delete />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Filter rates based on current filter
  const filteredRates = currentFilter === 'all' 
    ? gameRates 
    : gameRates.filter(rate => rate.game_mode === currentFilter);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Gali Game Rates
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage betting rates and win amounts for different game modes
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialog(true)}
          sx={{ borderRadius: 2 }}
        >
          Add Game Rate
        </Button>
      </Box>

      {/* Filter Buttons */}
      <Box display="flex" gap={2} mb={3} sx={{ overflowX: 'auto', pb: 1 }}>
        <Button 
          variant={currentFilter === 'all' ? 'contained' : 'outlined'} 
          onClick={() => setCurrentFilter('all')}
        >
          All
        </Button>
        <Button 
          variant={currentFilter === 'JANTRI' ? 'contained' : 'outlined'} 
          onClick={() => setCurrentFilter('JANTRI')}
          sx={{ color: currentFilter === 'JANTRI' ? 'white' : '#6366f1', bgcolor: currentFilter === 'JANTRI' ? '#6366f1' : 'transparent' }}
        >
          JANTRI
        </Button>
        <Button 
          variant={currentFilter === 'CROSS' ? 'contained' : 'outlined'} 
          onClick={() => setCurrentFilter('CROSS')}
          sx={{ color: currentFilter === 'CROSS' ? 'white' : '#10b981', bgcolor: currentFilter === 'CROSS' ? '#10b981' : 'transparent' }}
        >
          CROSS
        </Button>
        <Button 
          variant={currentFilter === 'OPEN' ? 'contained' : 'outlined'} 
          onClick={() => setCurrentFilter('OPEN')}
          sx={{ color: currentFilter === 'OPEN' ? 'white' : '#f59e0b', bgcolor: currentFilter === 'OPEN' ? '#f59e0b' : 'transparent' }}
        >
          OPEN
        </Button>
      </Box>

      {/* Game Rate Cards */}
      <Grid container spacing={3} mb={4}>
        {filteredRates.map((rate) => (
          <Grid item xs={12} sm={6} md={4} key={rate.id}>
            <GameRateCard
              gameMode={rate.game_mode}
              betCategory={rate.bet_category}
              configKey={rate.config_key}
              rate={rate.rate_display}
              betAmount={rate.bet_amount}
              winAmount={rate.win_amount}
              color={getGameModeColor(rate.game_mode)}
            />
          </Grid>
        ))}
        {filteredRates.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
              <Typography color="text.secondary">
                No game rates found for this filter. Create a new rate to get started.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Game Rates Table */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            All Game Rates ({filteredRates.length})
          </Typography>
          <Box sx={{ height: 500, width: '100%' }}>
            {filteredRates.length > 0 ? (
              <DataGrid
                rows={filteredRates}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-cell': {
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              />
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography color="text.secondary">No game rates available</Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Create Rate Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Game Rate</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Game Mode</InputLabel>
                <Select
                  value={formData.game_mode}
                  onChange={(e) => setFormData({...formData, game_mode: e.target.value})}
                  label="Game Mode"
                >
                  <MenuItem value="JANTRI">JANTRI</MenuItem>
                  <MenuItem value="CROSS">CROSS</MenuItem>
                  <MenuItem value="OPEN">OPEN</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Bet Category</InputLabel>
                <Select
                  value={formData.bet_category}
                  onChange={(e) => setFormData({...formData, bet_category: e.target.value})}
                  label="Bet Category"
                >
                  <MenuItem value="SINGLE">SINGLE</MenuItem>
                  <MenuItem value="ANDAR">ANDAR</MenuItem>
                  <MenuItem value="BAHAR">BAHAR</MenuItem>
                  <MenuItem value="NUMBER">NUMBER</MenuItem>
                  <MenuItem value="HARUP">HARUP</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Configuration</InputLabel>
                <Select
                  value={formData.config_key}
                  onChange={(e) => setFormData({...formData, config_key: e.target.value})}
                  label="Configuration"
                >
                  <MenuItem value="DEFAULT">Default</MenuItem>
                  <MenuItem value="WITH_PALTI">With Palti</MenuItem>
                  <MenuItem value="ANDAR">Andar Only</MenuItem>
                  <MenuItem value="BAHAR">Bahar Only</MenuItem>
                  <MenuItem value="BOTH">Both (Andar/Bahar)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Bet Amount"
                type="number"
                value={formData.bet_amount}
                onChange={(e) => setFormData({...formData, bet_amount: e.target.value})}
                helperText="Rate will be calculated automatically"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Win Amount"
                type="number"
                value={formData.win_amount}
                onChange={(e) => setFormData({...formData, win_amount: e.target.value})}
                helperText="Amount user will win"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Rate Display"
                value={formData.rate_display}
                onChange={(e) => setFormData({...formData, rate_display: e.target.value})}
                placeholder="e.g., 1:100"
                helperText="Auto-calculated, but can be edited"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateRate} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Rate Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Game Rate</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Game Mode</InputLabel>
                <Select
                  value={formData.game_mode}
                  onChange={(e) => setFormData({...formData, game_mode: e.target.value})}
                  label="Game Mode"
                >
                  <MenuItem value="JANTRI">JANTRI</MenuItem>
                  <MenuItem value="CROSS">CROSS</MenuItem>
                  <MenuItem value="OPEN">OPEN</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Bet Category</InputLabel>
                <Select
                  value={formData.bet_category}
                  onChange={(e) => setFormData({...formData, bet_category: e.target.value})}
                  label="Bet Category"
                >
                  <MenuItem value="SINGLE">SINGLE</MenuItem>
                  <MenuItem value="ANDAR">ANDAR</MenuItem>
                  <MenuItem value="BAHAR">BAHAR</MenuItem>
                  <MenuItem value="NUMBER">NUMBER</MenuItem>
                  <MenuItem value="HARUP">HARUP</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Configuration</InputLabel>
                <Select
                  value={formData.config_key}
                  onChange={(e) => setFormData({...formData, config_key: e.target.value})}
                  label="Configuration"
                >
                  <MenuItem value="DEFAULT">Default</MenuItem>
                  <MenuItem value="WITH_PALTI">With Palti</MenuItem>
                  <MenuItem value="ANDAR">Andar Only</MenuItem>
                  <MenuItem value="BAHAR">Bahar Only</MenuItem>
                  <MenuItem value="BOTH">Both (Andar/Bahar)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Bet Amount"
                type="number"
                value={formData.bet_amount}
                onChange={(e) => setFormData({...formData, bet_amount: e.target.value})}
                helperText="Rate will be calculated automatically"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Win Amount"
                type="number"
                value={formData.win_amount}
                onChange={(e) => setFormData({...formData, win_amount: e.target.value})}
                helperText="Amount user will win"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Rate Display"
                value={formData.rate_display}
                onChange={(e) => setFormData({...formData, rate_display: e.target.value})}
                placeholder="e.g., 1:100"
                helperText="Auto-calculated, but can be edited"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleEditRate} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default GaliGameRates;